<script setup lang="ts">
import { onMounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import BravoProgressSpinner from '@services/ui-component-library/components/BravoProgressSpinner.vue'
import { useSSO } from '@/composables/services/useSSO'

const route = useRoute()
const router = useRouter()
const { submitAcsLogin } = useSSO()

// Validate preflight parameter and perform ACS login
onMounted(async () => {
  // Get preflight parameter from route query
  const preflight = route.query.preflight as string
  const ptid = route.query.ptid as string

  // Validate preflight parameter exists and is a valid UUID format
  if (!preflight || !isValidUuid(preflight)) {
    console.warn('Invalid or missing preflight parameter, redirecting to home')
    await router.push('/')
    return
  }

  // Wait a tick to ensure component is mounted and visible before making request
  await nextTick()

  try {
    // Submit ACS login with preflight parameter
    await submitAcsLogin(preflight, ptid)
  } catch (error) {
    console.error('ACS login failed:', error)
    // On error, redirect to login page
    await router.push('/login')
  }
})

// Simple UUID validation function
const isValidUuid = (uuid: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  return uuidRegex.test(uuid)
}
</script>

<template>
    <div class="acs-view">
        <div class="acs-loading-container">
            <BravoProgressSpinner 
                style="width: 50px; height: 50px;" 
                strokeWidth="4" 
                animationDuration="1.5s"
            />
            <div class="acs-loading-text">
                Please wait while the login process completes
            </div>
        </div>
    </div>
</template>

<style scoped>
.acs-view {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: var(--color-surface, #ffffff);
}

.acs-loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;
  padding: 2rem;
}

.acs-loading-text {
  color: var(--color-text, #333333);
  font-size: 1rem;
  font-weight: 500;
  text-align: center;
  max-width: 300px;
}
</style>
